/**
 * Constantes de negócio externalizadas.
 *
 * No legado estes valores estavam espalhados como literais dentro de `construtorcs.html` e
 * `construtorps.html`. Externalizá-los permite versionar a tabela de preços: o documento de
 * proposta guarda `versaoConfiguracao`, o que torna possível reconstruir uma proposta antiga
 * com as constantes vigentes na data da emissão.
 *
 * Fonte: `docs/regras-negocio.md`, seção 12.
 */

import type {
  DuracaoContratoMeses,
  FocoAssessment,
  JanelaCreditoMeses,
  LicencaBackup,
  Moeda,
  PlanoSafeStorage,
  PlanoSuporte,
  Trilha,
} from './tipos.ts';

/** Regra de cobrança de um plano de suporte. */
export type RegraSuporte =
  | { readonly tipo: 'gratuito' }
  | { readonly tipo: 'fixo'; readonly valor: number; readonly moeda: Moeda }
  | {
      readonly tipo: 'percentual_com_piso';
      readonly percentual: number;
      readonly pisoMensal: number;
      readonly moeda: Moeda;
      /**
       * Consumo a partir do qual o percentual passa a valer. Abaixo dele, cobra-se o piso.
       * Em CS o comportamento é `max(piso, consumo * percentual)`, equivalente a um gatilho
       * implícito. Em PS o gatilho é explícito em R$ 6.000,00.
       */
      readonly gatilho: number | null;
    };

export interface ConfiguracaoTrilha {
  /** Moeda em que o vendedor informa os valores nesta trilha. */
  readonly moedaEntrada: Moeda;
  /** Margem por plano e duração, em pontos percentuais. `null` quando a trilha usa margem fixa. */
  readonly margens: Readonly<
    Record<PlanoSuporte, Readonly<Record<DuracaoContratoMeses, number>>>
  > | null;
  /** Margem única da trilha, em pontos percentuais. `null` quando a margem varia por plano. */
  readonly margemFixa: number | null;
  readonly margemFallback: number;
  readonly planosDisponiveis: readonly PlanoSuporte[];
  readonly regrasSuporte: Readonly<Partial<Record<PlanoSuporte, RegraSuporte>>>;
}

/**
 * Faixa de assinatura do eMaster SafeStorage.
 *
 * `usuariosAte` é o teto da faixa, e `null` significa faixa aberta — usada pela Enterprise, acima de
 * 50 usuários. `mensalidadeUsd` a `null` significa "sob consulta": o valor vem do vendedor.
 */
export interface FaixaSafeStorage {
  readonly plano: PlanoSafeStorage;
  readonly nomeComercial: string;
  readonly usuariosAte: number | null;
  readonly mensalidadeUsd: number | null;
  /** Perfil de cliente da faixa, exibido na proposta para o cliente se reconhecer nela. */
  readonly indicadoPara: string;
}

export interface Configuracao {
  readonly versao: string;
  readonly trilhas: Readonly<Record<Trilha, ConfiguracaoTrilha>>;
  readonly credito: {
    readonly moeda: Moeda;
    readonly janelasDisponiveis: readonly JanelaCreditoMeses[];
    readonly janelaPadrao: JanelaCreditoMeses;
  };
  readonly backup: {
    readonly moeda: Moeda;
    readonly precos: Readonly<Record<LicencaBackup, number>>;
  };
  readonly safestorage: {
    readonly moeda: Moeda;
    /** Faixas em ordem crescente de usuários. A ordem é o que permite achar a faixa por contagem. */
    readonly faixas: readonly FaixaSafeStorage[];
    /**
     * Plano de suporte cuja faixa de margem vale para o armazenamento do SafeStorage.
     *
     * O SafeStorage é vendido com a margem da faixa Developer, independente do plano de suporte
     * escolhido para o ambiente. Sem isso, um vendedor que deixasse o plano em Business venderia o
     * armazenamento a 30% e outro em Developer a 25% — o mesmo produto com dois preços, decidido por
     * um campo que trata de outro assunto.
     */
    readonly planoMargem: PlanoSuporte;
  };
  readonly cambio: {
    readonly fonte: string;
    readonly campo: string;
    readonly fallback: number;
  };
  /** Base de horas mensais usada pelo parser do CSV para converter utilização em horas. */
  readonly baseHorasMensais: number;
}

/**
 * Margens da trilha CS.
 *
 * Contrato de 36 meses reduz a margem em 2 pontos percentuais — desconto por fidelidade embutido.
 * O plano `nenhum` usa a mesma faixa do Developer, conforme a suposição A2.
 * Enterprise não expõe seletor de duração; a margem é 30% em ambos os casos.
 */
const MARGENS_CS = {
  nenhum: { 12: 25.0, 36: 23.0 },
  developer: { 12: 25.0, 36: 23.0 },
  business: { 12: 30.0, 36: 28.0 },
  enterprise: { 12: 30.0, 36: 30.0 },
} as const satisfies Record<PlanoSuporte, Record<DuracaoContratoMeses, number>>;

export const CONFIGURACAO_PADRAO: Configuracao = {
  versao: '2026-08',

  trilhas: {
    CS: {
      moedaEntrada: 'USD',
      margens: MARGENS_CS,
      margemFixa: null,
      margemFallback: 30.0,
      planosDisponiveis: ['nenhum', 'developer', 'business', 'enterprise'],
      regrasSuporte: {
        nenhum: { tipo: 'gratuito' },
        developer: {
          tipo: 'percentual_com_piso',
          percentual: 0.05,
          pisoMensal: 100,
          moeda: 'USD',
          gatilho: null,
        },
        business: {
          tipo: 'percentual_com_piso',
          percentual: 0.1,
          pisoMensal: 250,
          moeda: 'USD',
          gatilho: null,
        },
        enterprise: { tipo: 'fixo', valor: 5000, moeda: 'USD' },
      },
    },

    PS: {
      moedaEntrada: 'BRL',
      margens: null,
      // A margem maior do PS cobre o custo de participação em licitações e pregões, não o suporte.
      // Por isso permanece em 40% inclusive no modo sem plano de suporte (suposição A2).
      margemFixa: 40.0,
      margemFallback: 40.0,
      planosDisponiveis: ['nenhum', 'enterprise'],
      regrasSuporte: {
        nenhum: { tipo: 'gratuito' },
        enterprise: {
          tipo: 'percentual_com_piso',
          percentual: 0.1,
          pisoMensal: 600,
          moeda: 'BRL',
          gatilho: 6000,
        },
      },
    },
  },

  credito: {
    moeda: 'USD',
    janelasDisponiveis: [3, 4, 5, 6, 12],
    janelaPadrao: 3,
  },

  backup: {
    moeda: 'BRL',
    precos: {
      fileServer: 199.0,
      virtualServer: 199.0,
      sharepoint: 199.0,
      vmwareHyperv: 399.0,
    },
  },

  safestorage: {
    moeda: 'USD',
    faixas: [
      {
        plano: 'orbita',
        nomeComercial: 'SafeStorage Órbita',
        usuariosAte: 15,
        mensalidadeUsd: 79.9,
        indicadoPara:
          'Pequenas empresas, startups, escritórios de advocacia, consultorias e equipes enxutas.',
      },
      {
        plano: 'constelacao',
        nomeComercial: 'SafeStorage Constelação',
        usuariosAte: 30,
        mensalidadeUsd: 139.9,
        indicadoPara: 'Empresas em expansão, médias operações e departamentos integrados.',
      },
      {
        plano: 'singularidade',
        nomeComercial: 'SafeStorage Singularidade',
        usuariosAte: 50,
        mensalidadeUsd: 199.9,
        indicadoPara: 'Empresas consolidadas que necessitam de colaboração departamental intensa.',
      },
      {
        plano: 'enterprise',
        nomeComercial: 'SafeStorage Enterprise',
        usuariosAte: null,
        mensalidadeUsd: null,
        indicadoPara:
          'Grandes operações, múltiplos servidores ou integrações customizadas avançadas.',
      },
    ],
    planoMargem: 'developer',
  },

  cambio: {
    fonte: 'AwesomeAPI USD-BRL',
    campo: 'ask',
    fallback: 6.0,
  },

  baseHorasMensais: 730,
};

/** Nome comercial de cada plano, usado na renderização da proposta. */
export const NOME_COMERCIAL_PLANO: Readonly<Record<PlanoSuporte, string>> = {
  nenhum: 'Sem plano de suporte gerenciado',
  developer: 'SafePlace Developer',
  business: 'SafePlace Business',
  enterprise: 'SafePlace Enterprise',
};

/** Nome comercial de cada faixa do SafeStorage, para rótulo de campo e de tabela. */
export const NOME_COMERCIAL_SAFESTORAGE: Readonly<Record<PlanoSafeStorage, string>> = {
  orbita: 'SafeStorage Órbita',
  constelacao: 'SafeStorage Constelação',
  singularidade: 'SafeStorage Singularidade',
  enterprise: 'SafeStorage Enterprise',
};

/** Nome comercial de cada assessment, usado no formulário e na proposta. */
export const NOME_COMERCIAL_ASSESSMENT: Readonly<Record<FocoAssessment, string>> = {
  custos: 'Assessment SafePlace — Otimização de Custos',
  seguranca: 'Assessment SafePlace — Segurança',
};

/**
 * Nome curto de cada foco, para compor título quando a proposta traz os dois.
 *
 * Existe porque concatenar o nome comercial completo produziria "Assessment SafePlace — Otimização de
 * Custos e Assessment SafePlace — Segurança".
 */
export const FOCO_ASSESSMENT_CURTO: Readonly<Record<FocoAssessment, string>> = {
  custos: 'Otimização de Custos',
  seguranca: 'Segurança',
};

/** Pilar do Well-Architected Framework correspondente a cada foco. */
export const PILAR_ASSESSMENT: Readonly<Record<FocoAssessment, string>> = {
  custos: 'Cost Optimization',
  seguranca: 'Security',
};
